import React, { useState } from "react";
import {
    Sprout,
    FileText,
    Folder,
    Book,
    Briefcase,
    Target,
    BarChart,
    Users,
    MapPin,
    Calendar,
    Bell,
    Settings,
    Star,
    Heart,
    Zap,
    TrendingUp,
    Award,
    Lightbulb,
    PieChart,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export const NOTEBOOK_COLORS = [
    "#FFB6C1",
"#FEC2C0",
"#DDD8FF",
"#FFD1E8",
"#EAEDFF",
"#CFFFE5",
"#A7ACFB",
"#FFD2E6",
"#FDCCFE",
"#DDF1FD",
"#F9D5FD",
"#FEC4CC",
"#FEDACB",
"#FCC8F5",
"#FEFFCF",
"#FFDAB3",
"#E1B3FF",
"#FDC1C1",
"#E7D2FF",
"#FEBDDE",
"#FEC7D9",
"#F0FFDE",
"#F6FDD2",
"#CDCAFC",
"#C2F9E3",
"#EEC4FE",
"#BCFDF4",
"#FDE6C6"
];

export const NOTEBOOK_ICONS = [
    "Sprout",
    "FileText",
    "Folder",
    "Book",
    "Briefcase",
    "Target",
    "BarChart",
    "Users",
    "MapPin",
    "Calendar",
    "Bell",
    "Settings",
    "Star",
    "Heart",
    "Zap",
    "TrendingUp",
    "Award",
    "Lightbulb",
    "PieChart",
];

export const iconComponents: Record<string, LucideIcon> = {
    Sprout,
    FileText,
    Folder,
    Book,
    Briefcase,
    Target,
    BarChart,
    Users,
    MapPin,
    Calendar,
    Bell,
    Settings,
    Star,
    Heart,
    Zap,
    TrendingUp,
    Award,
    Lightbulb,
    PieChart,
};

export interface ColorPickerProps {
    value: string;
    onChange: (color: string) => void;
}

export const ColorPicker = ({ value, onChange }: ColorPickerProps) => {
    const [hoveredColor, setHoveredColor] = useState<string | null>(null);

    return (
        <div className="grid grid-cols-12 gap-2">
            {NOTEBOOK_COLORS.map((color, idx) => {
                const isSelected = value === color;
                const isHovered = hoveredColor === color;

                const getBorderRadius = () => {
                    if (isSelected) return "rounded-sm";
                    if (isHovered) return "rounded-md";
                    return "rounded-lg";
                };

                const getBorderColor = () => {
                    if (isSelected || isHovered) return "border-red-600";
                    return "border-gray-200";
                };

                const getInnerBorderRadius = () => {
                    if (isSelected || isHovered) return "4px";
                    return "50%";
                };

                return (
                    <button
                        key={idx}
                        type="button"
                        onClick={() => onChange(color)}
                        onMouseEnter={() => setHoveredColor(color)}
                        onMouseLeave={() => setHoveredColor(null)}
                        className={`w-10 h-10 border flex items-center justify-center bg-white transition-all duration-300 ease-in-out ${
                            isSelected || isHovered ? "scale-105" : "scale-100"
                        } ${getBorderRadius()} ${getBorderColor()}`}
                    >
                        <div
                            className="w-6 h-6 transition-all duration-300 ease-in-out"
                            style={{
                                backgroundColor: color,
                                borderRadius: getInnerBorderRadius(),
                            }}
                        />
                    </button>
                );
            })}
        </div>
    );
};

export interface IconPickerProps {
    value: string;
    onChange: (icon: string) => void;
}

export const IconPicker = ({ value, onChange }: IconPickerProps) => {
    const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);

    return (
        <div className="grid grid-cols-12 gap-2">
            {NOTEBOOK_ICONS.map((iconName, idx) => {
                const IconComponent = iconComponents[iconName];
                if (!IconComponent) return null;

                const isSelected = value === iconName;
                const isHovered = hoveredIcon === iconName;

                const getBorderRadius = () => {
                    if (isSelected) return "rounded-sm";
                    if (isHovered) return "rounded-md";
                    return "rounded-lg";
                };

                const getBorderColor = () => {
                    if (isSelected) return "border-transparent";
                    if (isHovered) return "border-gray-600";
                    return "border-gray-200";
                };

                const getBackground = () => {
                    if (isSelected) return "bg-[#A5F455]";
                    return "bg-white";
                };

                return (
                    <button
                        key={idx}
                        type="button"
                        onClick={() => onChange(iconName)}
                        onMouseEnter={() => setHoveredIcon(iconName)}
                        onMouseLeave={() => setHoveredIcon(null)}
                        className={`w-10 h-10 border flex items-center justify-center transition-all duration-300 ease-in-out ${
                            isSelected || isHovered ? "scale-105" : "scale-100"
                        } ${getBorderRadius()} ${getBorderColor()} ${getBackground()}`}
                    >
                        <IconComponent
                            size={18}
                            className="text-[#1b1b1d] transition-transform duration-300 ease-in-out"
                        />
                    </button>
                );
            })}
        </div>
    );
};
